import { useCallback, useLayoutEffect, useRef } from 'react';
import { useMediaQuery } from './use-media-query';
import type {
  NativeVirtualKeyboard,
  TextareaKeyboardOptions,
} from '../types/textarea-keyboard.types';

const supportsVisibility = (value: unknown): value is NativeVirtualKeyboard =>
  typeof value === 'object' &&
  value !== null &&
  'hide' in value &&
  typeof value.hide === 'function' &&
  'show' in value &&
  typeof value.show === 'function';

/**
 * Coordinates native keyboard requests and optional retained-focus dismissal.
 * Keeps a successfully hidden editor in manual mode until an explicit return.
 * Restores browser behavior on editor interaction or focus transfer and can clear
 * retained touch focus after native dismissal. Unsupported APIs use normal focus.
 * @param options - Native editor and interaction availability.
 * @returns Hide (true when requested) and resume actions; neither moves focus.
 */
export const useTextareaKeyboard = ({
  inputRef,
  disabled,
  dismissOnKeyboardHide = false,
}: TextareaKeyboardOptions) => {
  const touch = useMediaQuery('(pointer: coarse)');
  const release = useRef<(() => void) | undefined>();
  const hidden = useRef(false);
  const hiddenInset = useRef(0);
  const restorePolicy = useCallback(() => {
    release.current?.();
  }, []);
  const reset = useCallback(() => {
    restorePolicy();
    hidden.current = false;
  }, [restorePolicy]);

  useLayoutEffect(() => {
    const input = inputRef.current;
    const viewport = window.visualViewport;
    if (disabled || !dismissOnKeyboardHide || !touch || !input || !viewport) {
      return;
    }
    let width = window.innerWidth;
    let baseline = Math.max(window.innerHeight, viewport.height);
    let wasVisible = false;
    let frame = 0;
    const resetObservation = () => {
      wasVisible = false;
      window.cancelAnimationFrame(frame);
    };
    const sample = () => {
      if (viewport.scale !== 1 || window.innerWidth !== width) {
        width = window.innerWidth;
        baseline = Math.max(window.innerHeight, viewport.height);
        wasVisible = false;
        return;
      }
      baseline = Math.max(baseline, window.innerHeight, viewport.height);
      if (input.ownerDocument.activeElement !== input) {
        wasVisible = false;
        return;
      }
      const inset = baseline - viewport.height;
      if (inset > 80) wasVisible = true;
      if (!wasVisible || inset > 2 || (hidden.current && release.current)) {
        return;
      }
      if (release.current) {
        // A rapid requested return can cross the fully hidden viewport before
        // reopening. Wait for its bounded request rather than treating it as Back.
        frame = window.requestAnimationFrame(sample);
        return;
      }
      wasVisible = false;
      input.blur();
    };
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(sample);
    };
    schedule();
    viewport.addEventListener('resize', schedule);
    window.addEventListener('resize', schedule);
    input.addEventListener('focus', schedule);
    input.addEventListener('blur', resetObservation);
    window.addEventListener('pagehide', resetObservation);
    return () => {
      resetObservation();
      viewport.removeEventListener('resize', schedule);
      window.removeEventListener('resize', schedule);
      input.removeEventListener('focus', schedule);
      input.removeEventListener('blur', resetObservation);
      window.removeEventListener('pagehide', resetObservation);
    };
  }, [disabled, dismissOnKeyboardHide, inputRef, touch]);

  const request = useCallback(
    (mode: 'hide' | 'show', viaPointer = false) => {
      const input = inputRef.current;
      const keyboard =
        'virtualKeyboard' in navigator ? navigator.virtualKeyboard : undefined;
      const viewport = window.visualViewport;
      if (
        disabled ||
        !input ||
        input !== document.activeElement ||
        !supportsVisibility(keyboard) ||
        (viewport && viewport.scale !== 1)
      ) {
        return false;
      }
      release.current?.();
      const oldPolicy = input.getAttribute('virtualkeyboardpolicy');
      const height = () => viewport?.height ?? window.innerHeight;
      const baseline = Math.max(window.innerHeight, height());
      const startInset = baseline - height();
      if (mode === 'hide' && startInset > 80) hiddenInset.current = startInset;
      const returnInset = Math.max(80, hiddenInset.current);
      let returnHasMoved = startInset < returnInset - 2;
      const width = window.innerWidth;
      let moved = false;
      let active = true;
      let frame = 0;
      const resized = () => {
        moved = true;
      };
      const windowResized = () => {
        if (window.innerWidth !== width) restorePolicy();
      };
      const restore = () => {
        if (oldPolicy === null) input.removeAttribute('virtualkeyboardpolicy');
        else input.setAttribute('virtualkeyboardpolicy', oldPolicy);
        release.current = undefined;
      };
      const finish = (keepHidden = false) => {
        if (!active) return;
        active = false;
        window.cancelAnimationFrame(frame);
        window.clearTimeout(timeout);
        viewport?.removeEventListener('resize', resized);
        window.removeEventListener('resize', windowResized);
        if (keepHidden) release.current = restore;
        else restore();
      };
      // Unanswered requests are bounded. A confirmed hide keeps manual mode
      // until an explicit return or lifecycle cleanup releases ownership.
      const timeout = window.setTimeout(() => {
        if (mode === 'hide') hidden.current = false;
        finish();
      }, 1500);
      const sample = () => {
        if (!active) return;
        const inset = baseline - height();
        if (inset < returnInset - 2) returnHasMoved = true;
        const completed =
          mode === 'hide'
            ? height() >= baseline - 2
            : moved && returnHasMoved && inset > 80 && inset >= returnInset - 2;
        if (completed) finish(mode === 'hide');
        else frame = window.requestAnimationFrame(sample);
      };
      release.current = finish;
      hidden.current = mode === 'hide';
      viewport?.addEventListener('resize', resized);
      window.addEventListener('resize', windowResized);
      // A direct editor gesture uses normal browser activation after the
      // consuming pointer handler has prepared its replacement layout.
      if (!viaPointer) {
        input.setAttribute('virtualkeyboardpolicy', 'manual');
        keyboard[mode]();
      }
      frame = window.requestAnimationFrame(sample);
      return true;
    },
    [disabled, inputRef, restorePolicy]
  );

  useLayoutEffect(() => {
    if (disabled) {
      reset();
      return;
    }
    const input = inputRef.current;
    const returnFromPointer = () => {
      if ((hidden.current || release.current) && request('show', true)) return;
      reset();
    };
    input?.addEventListener('pointerdown', returnFromPointer);
    input?.addEventListener('blur', reset);
    input?.addEventListener('compositionstart', reset);
    window.addEventListener('pagehide', restorePolicy);
    return () => {
      reset();
      input?.removeEventListener('pointerdown', returnFromPointer);
      input?.removeEventListener('blur', reset);
      input?.removeEventListener('compositionstart', reset);
      window.removeEventListener('pagehide', restorePolicy);
    };
  }, [disabled, inputRef, request, reset, restorePolicy]);

  const hideKeyboard = useCallback(() => request('hide'), [request]);
  const resumeKeyboard = useCallback(() => {
    if (hidden.current && !request('show')) reset();
  }, [request, reset]);
  return { hideKeyboard, resumeKeyboard };
};
